
import { useState } from 'react';
import { Col, Row, Badge } from 'react-bootstrap';
import { FaDownload, FaEye, FaFileInvoice } from 'react-icons/fa';
import DataTable from 'react-data-table-component';
import LayoutFilter from '../../data/LayoutFilter';
import LayoutTable from '../../data/LayoutTable';
import ReactSelect from '../../data/reactSelect';
import { InputDefault } from '../../data/inputDefault';
import tableStyleDefault from '../../../Services/tableStyleDefault';
import CustomLoader from '../../data/customLoader';
import { paginationOptions } from '../../data/PagineteInfo';
import { IListEmitirNfe, INfeResponse } from '../../../interfaces/INfe';
import { useQueryFetchPages } from '../../../apiQuery/useQueryFetchPages';
import { useAppSelector } from '../../../redux/store';
import Globals from '../../../Globals.json';
import * as S from './styled';

export default function ListEmitirNfe({ title, onEmitir }: IListEmitirNfe) {
  const storeMe = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);
  
  const [page, setPage] = useState(1);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [nfeNumber, setNfeNumber] = useState<string>('');
  const [clienteNome, setClienteNome] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  // Simulação de dados - você pode substituir por uma consulta real à API
  const {
    data: nfesData,
    isLoading: isLoadingNfes,
    isError: isErrorNfes,
  } = useQueryFetchPages<any>(['nfes'], `${Globals.api.nfeEmitir}/historico`, {
    page: page,
    queryParams: {
      store_id: storeMe?.storeActive?.id,
      ...(nfeNumber && { 'filter[nfe_number]': nfeNumber }),
      ...(clienteNome && { 'filter[cliente_nome]': clienteNome }),
      ...(status && { 'filter[status]': status }),
      ...(dataInicio && { 'filter[data_inicio]': dataInicio }),
      ...(dataFim && { 'filter[data_fim]': dataFim }),
    },
  });

  const statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'aprovado', label: 'Aprovado' },
    { value: 'rejeitado', label: 'Rejeitado' },
    { value: 'processando', label: 'Processando' },
    { value: 'cancelado', label: 'Cancelado' }
  ];

  const getStatusBadge = (status: string) => {
    const badgeVariants: { [key: string]: string } = {
      'aprovado': 'success',
      'rejeitado': 'danger',
      'processando': 'warning',
      'cancelado': 'secondary'
    };
    
    return (
      <Badge bg={badgeVariants[status] || 'secondary'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const handleDownloadPdf = (danfeUrl: string) => {
    window.open(danfeUrl, '_blank');
  };

  const handleDownloadXml = (xmlUrl: string) => {
    window.open(xmlUrl, '_blank');
  };

  const handleViewDanfe = (danfeUrl: string) => {
    window.open(danfeUrl, '_blank');
  };

  const columns = [
    {
      name: 'NFe',
      center: true,
      sortable: true,
      grow: 1,
      selector: (row: any) => row.body?.nfe || row.nfe || '---',
    },
    {
      name: 'Série',
      center: true,
      grow: 1,
      selector: (row: any) => row.body?.serie || row.serie || '---',
    },
    {
      name: 'Cliente',
      center: false,
      sortable: true,
      grow: 2,
      selector: (row: any) => row.cliente_nome || 'Cliente não informado',
    },
    {
      name: 'Chave de Acesso',
      center: false,
      grow: 3,
      cell: (row: any) => {
        const chave = row.body?.chave || row.chave;
        return chave ? (
          <span style={{ fontSize: '11px', fontFamily: 'monospace' }}>
            {chave}
          </span>
        ) : '---';
      }
    },
    {
      name: 'Status',
      center: true,
      grow: 1,
      cell: (row: any) => getStatusBadge(row.body?.status || row.status || 'processando'),
    },
    {
      name: 'Data/Hora',
      center: true,
      sortable: true,
      grow: 2,
      selector: (row: any) => {
        const date = row.created_at || row.data_emissao;
        return date ? new Date(date).toLocaleString('pt-BR') : '---';
      }
    },
    {
      name: 'Ações',
      center: true,
      grow: 2,
      cell: (row: any) => {
        const hasUrls = row.body?.danfe || row.body?.xml;
        return (
          <div className="actions flex gap-2">
            {row.body?.danfe && (
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => handleViewDanfe(row.body.danfe)}
                title="Visualizar DANFE"
              >
                <FaEye size={12} />
              </button>
            )}
            {row.body?.danfe && (
              <button
                className="btn btn-sm btn-outline-success"
                onClick={() => handleDownloadPdf(row.body.danfe)}
                title="Download PDF"
              >
                <FaDownload size={12} />
              </button>
            )}
            {row.body?.xml && (
              <button
                className="btn btn-sm btn-outline-info"
                onClick={() => handleDownloadXml(row.body.xml)}
                title="Download XML"
              >
                <FaFileInvoice size={12} />
              </button>
            )}
            {!hasUrls && (
              <span className="text-muted small">Não disponível</span>
            )}
          </div>
        );
      },
      button: true,
    },
  ];

  const handleAdd = () => {
    onEmitir();
  };

  const onClickOpenFilter = () => {
    setOpenFilter(!openFilter);
  };

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page);
  };

  return (
    <>
      <LayoutFilter
        title={title}
        onClickAdd={handleAdd}
        onClickOpenFilter={onClickOpenFilter}
        openFilter={openFilter}
      >
        <S.Wrapper>
          {openFilter && (
            <div className="min-screen">
              <InputDefault
                label="Número NFe"
                type="text"
                state={nfeNumber}
                setState={setNfeNumber}
                placeholder="Número da NFe"
              />

              <InputDefault
                label="Nome do Cliente"
                type="text"
                state={clienteNome}
                setState={setClienteNome}
                placeholder="Nome do cliente"
              />

              <ReactSelect
                label="Status"
                isClearable={true}
                placeholder="Status da NFe"
                options={statusOptions}
                state={status}
                setState={setStatus}
              />

              <InputDefault
                label="Data Início"
                type="date"
                state={dataInicio}
                setState={setDataInicio}
                placeholder="Data início"
              />

              <InputDefault
                label="Data Fim"
                type="date"
                state={dataFim}
                setState={setDataFim}
                placeholder="Data fim"
              />
            </div>
          )}

          <div className="max-screen">
            <InputDefault
              label="Número NFe"
              type="text"
              state={nfeNumber}
              setState={setNfeNumber}
              placeholder="Número da NFe"
            />

            <InputDefault
              label="Nome do Cliente"
              type="text"
              state={clienteNome}
              setState={setClienteNome}
              placeholder="Nome do cliente"
            />

            <ReactSelect
              label="Status"
              isClearable={true}
              placeholder="Status da NFe"
              options={statusOptions}
              state={status}
              setState={setStatus}
            />

            <InputDefault
              label="Data Início"
              type="date"
              state={dataInicio}
              setState={setDataInicio}
              placeholder="Data início"
            />

            <InputDefault
              label="Data Fim"
              type="date"
              state={dataFim}
              setState={setDataFim}
              placeholder="Data fim"
            />
          </div>
        </S.Wrapper>
      </LayoutFilter>

      <LayoutTable>
        <DataTable
          columns={columns}
          data={nfesData?.data || []}
          striped
          pagination
          paginationServer
          paginationTotalRows={nfesData?.meta?.total}
          paginationDefaultPage={1}
          paginationPerPage={15}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          customStyles={tableStyleDefault}
          paginationComponentOptions={paginationOptions}
          progressPending={isLoadingNfes}
          progressComponent={<CustomLoader />}
          noDataComponent="Nenhuma nota fiscal foi encontrada."
        />
      </LayoutTable>
    </>
  );
}